import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Camera, QrCode, ClipboardPaste, UserPlus, ImageUp, Images, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { submitCapture } from "@/lib/offline";
import { prepareImage } from "@/lib/image";
import { PageHeader, Field } from "@/components/common";
import ActiveEventPicker from "@/components/ActiveEventPicker";
import QrScanner from "@/components/QrScanner";

const MODES = [["card", "Card photo", Camera], ["qr", "QR code", QrCode], ["paste", "Paste", ClipboardPaste], ["manual", "Manual", UserPlus]];

function savedOffline() {
  toast.message("Saved on this device, waiting to upload", { description: "Not backed up until synced. It uploads automatically when you're back online while the app is open." });
}

function ImageSlot({ label, file, setFile, testid }) {
  const preview = file ? URL.createObjectURL(file) : null;
  return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-3">
      <p className="cd-label">{label}</p>
      {preview ? (
        <div className="relative">
          <img src={preview} alt={label} className="h-40 w-full rounded-xl object-contain bg-slate-50" />
          <button onClick={() => setFile(null)} data-testid={`${testid}-remove`} className="absolute right-2 top-2 rounded-full bg-white/90 px-2 py-1 text-xs font-semibold shadow">Remove</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <label className="cd-btn-primary cursor-pointer"><Camera className="h-5 w-5" />Camera
            <input data-testid={`${testid}-camera-input`} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <label className="cd-btn-secondary cursor-pointer"><ImageUp className="h-5 w-5" />Choose
            <input data-testid={`${testid}-file-input`} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
      )}
    </div>
  );
}

function CardMode() {
  const nav = useNavigate();
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const files = [await prepareImage(front), ...(back ? [await prepareImage(back)] : [])];
      const r = await submitCapture({ kind: "upload", files, names: files.map((f) => f.name), source_type: "card_photo" });
      if (r.synced) nav(`/captures/${r.result.id}`);
      else { savedOffline(); setFront(null); setBack(null); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <p className="flex gap-2 rounded-xl bg-slate-100 p-3 text-sm text-slate-600"><Info className="mt-0.5 h-4 w-4 shrink-0" />“Camera” opens your phone's camera. If it's unavailable or permission is blocked, use “Choose” to pick a photo instead.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <ImageSlot label="Front (required)" file={front} setFile={setFront} testid="card-front" />
        <ImageSlot label="Back (optional)" file={back} setFile={setBack} testid="card-back" />
      </div>
      <button data-testid="card-submit-btn" disabled={!front || busy} onClick={go} className="cd-btn-primary w-full">{busy ? "Uploading…" : "Save & extract details"}</button>
      <Link to="/batch" className="flex items-center justify-center gap-2 text-sm font-medium text-teal-700"><Images className="h-4 w-4" />Have many cards? Use batch upload</Link>
    </div>
  );
}

function QrMode() {
  const nav = useNavigate();
  const [res, setRes] = useState(null);
  const [raw, setRaw] = useState("");
  const handle = async (content) => {
    setRaw(content);
    try {
      const r = await submitCapture({ kind: "qr", text: content });
      if (!r.synced) { savedOffline(); return; }
      setRes(r.result);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const importSite = async () => {
    const r = await submitCapture({ kind: "text", text: res.fields.website });
    if (r.synced) nav(`/captures/${r.result.id}`); else savedOffline();
  };
  const saveUrl = async () => {
    const r = await submitCapture({ kind: "manual", data: { website: res.fields.website } });
    if (r.synced) nav(`/contacts/${r.result.contact_id}?review=1`); else savedOffline();
  };
  const extractText = async () => {
    const r = await submitCapture({ kind: "text", text: raw });
    if (r.synced) nav(`/captures/${r.result.id}`); else savedOffline();
  };
  if (!res) return <QrScanner onResult={handle} />;
  return (
    <div className="cd-card space-y-4 cd-fade" data-testid="qr-result">
      <div className="flex items-center justify-between"><p className="font-bold">QR result</p><span data-testid="qr-type" className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold uppercase">{res.qr_type}</span></div>
      <p data-testid="qr-message" className="text-sm text-slate-700">{res.message}</p>
      <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-xl bg-slate-50 p-3 text-xs text-slate-600">{raw}</pre>
      <div className="flex flex-wrap gap-2">
        {res.contact_id && <button data-testid="qr-open-contact-btn" onClick={() => nav(`/contacts/${res.contact_id}?review=1`)} className="cd-btn-primary">Review contact</button>}
        {res.qr_type === "website" && <>
          <button data-testid="qr-import-website-btn" onClick={importSite} className="cd-btn-primary">Import public business info</button>
          <button data-testid="qr-save-url-btn" onClick={saveUrl} className="cd-btn-secondary">Save URL only</button>
        </>}
        {res.qr_type === "text" && <button data-testid="qr-extract-text-btn" onClick={extractText} className="cd-btn-primary">Extract details with AI</button>}
        <button data-testid="qr-scan-again-btn" onClick={() => setRes(null)} className="cd-btn-ghost">Scan another</button>
      </div>
    </div>
  );
}

function PasteMode() {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await submitCapture({ kind: "text", text });
      if (!r.synced) { savedOffline(); setText(""); }
      else if (r.result.status === "complete" && r.result.contact_ids?.length) nav(`/contacts/${r.result.contact_ids[0]}?review=1`);
      else nav(`/captures/${r.result.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <Field label="Paste an email, website, signature or any contact text" hint="Websites are fetched safely on the server (public pages only). Nothing in pasted text is treated as instructions.">
        <textarea data-testid="paste-textarea" rows={8} className="cd-textarea" value={text} onChange={(e) => setText(e.target.value)} placeholder={"e.g. anna@company.com\nor www.company.com\nor a full email signature"} />
      </Field>
      <button data-testid="paste-submit-btn" disabled={!text.trim() || busy} onClick={go} className="cd-btn-primary w-full">{busy ? "Saving…" : "Save & extract"}</button>
    </div>
  );
}

function ManualMode() {
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: "", company: "", job_title: "", email: "", phone: "", website: "" });
  const go = async () => {
    try {
      const r = await submitCapture({ kind: "manual", data: f });
      if (r.synced) nav(`/contacts/${r.result.contact_id}?saved=1`); else { savedOffline(); setF({ full_name: "", company: "", job_title: "", email: "", phone: "", website: "" }); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      {[["full_name", "Full name"], ["company", "Company"], ["job_title", "Job title"], ["email", "Email"], ["phone", "Phone"], ["website", "Website"]].map(([k, l]) => (
        <Field key={k} label={l}><input data-testid={`manual-${k}-input`} className="cd-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></Field>
      ))}
      <button data-testid="manual-submit-btn" disabled={!f.full_name && !f.company && !f.email} onClick={go} className="cd-btn-primary w-full">Save contact</button>
    </div>
  );
}

export default function Capture() {
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") || "card";
  return (
    <div className="max-w-2xl">
      <PageHeader title="Capture" subtitle="New captures are saved first, then details are extracted in the background." />
      <div className="mb-4"><ActiveEventPicker /></div>
      <div className="mb-5 grid grid-cols-4 gap-1 rounded-2xl bg-slate-100 p-1">
        {MODES.map(([m, l, I]) => (
          <button key={m} data-testid={`capture-mode-${m}`} onClick={() => setParams({ mode: m })}
            className={`flex h-12 flex-col items-center justify-center rounded-xl text-xs font-semibold sm:flex-row sm:gap-2 sm:text-sm ${mode === m ? "bg-white text-teal-700 shadow-sm" : "text-slate-600"}`}>
            <I className="h-4 w-4" />{l}
          </button>
        ))}
      </div>
      {mode === "card" && <CardMode />}
      {mode === "qr" && <QrMode />}
      {mode === "paste" && <PasteMode />}
      {mode === "manual" && <ManualMode />}
    </div>
  );
}
